import React, { useState, useEffect, useMemo } from 'react';
import AutoAwesome from '@mui/icons-material/AutoAwesome';

const PHASE_TITLES = {
  services: 'Essential Services',
  processes: 'Critical Processes',
  cyberattack: 'Cyberattack Impact',
};

const SERVICE_FIELDS = [
  { key: 'service_name', label: 'Name', type: 'text' },
  { key: 'service_description', label: 'Description', type: 'text' },
  { key: 'user_count_score', label: 'Reach', type: 'score' },
  { key: 'frequency_score', label: 'Frequency', type: 'score' },
  { key: 'replaceability_score', label: 'Replaceability', type: 'score' },
  { key: 'interdependency_score', label: 'Interdependency', type: 'score' },
  { key: 'time_to_impact_score', label: 'Time to Impact', type: 'score' },
];

const PROCESS_FIELDS = [
  { key: 'process_name', label: 'Name', type: 'text' },
  { key: 'process_description', label: 'Description', type: 'text' },
  { key: 'automation_level', label: 'Automation Level', type: 'score' },
  { key: 'it_ot_systems', label: 'IT/OT Systems', type: 'tags' },
];

const CYBER_FIELDS = [
  { key: 'security_impact', label: 'National Security Impact', type: 'score' },
  { key: 'economy_impact', label: 'Economic Impact', type: 'score' },
  { key: 'health_impact', label: 'Health & Safety Impact', type: 'score' },
  { key: 'privacy_impact', label: 'Privacy Impact', type: 'score' },
  { key: 'notes', label: 'Notes', type: 'text' },
];

const SCORE_COLORS = {
  1: 'bg-emerald-500', 2: 'bg-blue-400', 3: 'bg-amber-400', 4: 'bg-orange-500', 5: 'bg-red-500',
};

function ScoreBadge({ value }) {
  if (!value || value < 1 || value > 5) return <span className="text-xs text-slate-400">--</span>;
  return (
    <span className={`inline-flex items-center justify-center w-6 h-6 rounded text-[10px] font-bold text-white ${SCORE_COLORS[value]}`}>
      {value}
    </span>
  );
}

function TagList({ tags }) {
  if (!tags?.length) return <span className="text-xs text-slate-400">None</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {tags.map(t => (
        <span key={t} className="px-1.5 py-0.5 text-[10px] rounded-full bg-slate-200 text-slate-600">{t}</span>
      ))}
    </div>
  );
}

function valuesEqual(a, b, type) {
  if (type === 'tags') {
    const sa = [...(a || [])].sort().join(',');
    const sb = [...(b || [])].sort().join(',');
    return sa === sb;
  }
  if (type === 'score') return Number(a || 0) === Number(b || 0);
  return (a || '') === (b || '');
}

function renderValue(value, type) {
  if (type === 'score') return <ScoreBadge value={value} />;
  if (type === 'tags') return <TagList tags={value} />;
  return <span className="text-sm text-slate-700">{value || <span className="text-slate-400">--</span>}</span>;
}

// Compute diff for array-based phases (services, processes)
function computeArrayDiff(currentItems, aiItems, fields) {
  const maxLen = Math.max(currentItems.length, aiItems.length);
  const items = [];
  for (let i = 0; i < maxLen; i++) {
    const cur = currentItems[i] || null;
    const ai = aiItems[i] || null;
    const isNew = !cur;
    const isRemoved = !ai;
    const fieldDiffs = fields.map(f => {
      const curVal = cur ? cur[f.key] : undefined;
      const aiVal = ai ? ai[f.key] : undefined;
      const same = cur && ai ? valuesEqual(curVal, aiVal, f.type) : false;
      return { ...f, curVal, aiVal, same };
    });
    items.push({ index: i, cur, ai, isNew, isRemoved, fieldDiffs });
  }
  return items;
}

// Compute diff for object-based phase (cyberattack)
function computeObjectDiff(current, ai, fields) {
  return fields.map(f => {
    const curVal = current[f.key];
    const aiVal = ai[f.key];
    const same = valuesEqual(curVal, aiVal, f.type);
    return { ...f, curVal, aiVal, same };
  });
}

function AISuggestionReviewModal({ isOpen, onClose, phase, currentData, aiData, onApply }) {
  // selections: for array phases -> { [itemIdx]: { [fieldKey]: bool } }, for object phase -> { [fieldKey]: bool }
  const [selections, setSelections] = useState({});
  // For multi-phase (auto-profile): tabs
  const phases = Array.isArray(phase) ? phase : (phase ? [phase] : []);
  const [activeTab, setActiveTab] = useState(phases[0] || 'services');
  const isMulti = phases.length > 1;

  // Reset selections when data changes
  useEffect(() => {
    if (!isOpen) return;
    const newSel = {};
    for (const ph of phases) {
      newSel[ph] = buildDefaultSelections(ph, currentData?.[ph], aiData?.[ph]);
    }
    setSelections(newSel);
    setActiveTab(phases[0] || 'services');
  }, [isOpen]);

  function buildDefaultSelections(ph, cur, ai) {
    const fields = getFieldsForPhase(ph);
    if (ph === 'cyberattack') {
      const sel = {};
      fields.forEach(f => {
        if (!valuesEqual(cur?.[f.key], ai?.[f.key], f.type)) sel[f.key] = true;
      });
      return sel;
    }
    // Array phases
    const curArr = cur || [];
    const aiArr = ai || [];
    const maxLen = Math.max(curArr.length, aiArr.length);
    const sel = {};
    for (let i = 0; i < maxLen; i++) {
      sel[i] = {};
      const c = curArr[i];
      const a = aiArr[i];
      if (!c) { sel[i]._include = true; continue; } // new item: default include
      if (!a) { sel[i]._include = false; continue; } // removed: default exclude (keep current)
      fields.forEach(f => {
        if (!valuesEqual(c[f.key], a[f.key], f.type)) sel[i][f.key] = true;
      });
    }
    return sel;
  }

  function getFieldsForPhase(ph) {
    if (ph === 'services') return SERVICE_FIELDS;
    if (ph === 'processes') return PROCESS_FIELDS;
    return CYBER_FIELDS;
  }

  const toggleField = (ph, itemIdx, fieldKey) => {
    setSelections(prev => {
      const phSel = { ...(prev[ph] || {}) };
      if (itemIdx === null) {
        // Object phase
        phSel[fieldKey] = !phSel[fieldKey];
      } else {
        phSel[itemIdx] = { ...(phSel[itemIdx] || {}) };
        if (fieldKey === '_include') {
          phSel[itemIdx]._include = !phSel[itemIdx]._include;
        } else {
          phSel[itemIdx][fieldKey] = !phSel[itemIdx][fieldKey];
        }
      }
      return { ...prev, [ph]: phSel };
    });
  };

  const handleKeepCurrent = () => onClose();

  const handleApplyAll = () => {
    const merged = {};
    for (const ph of phases) {
      const ai = aiData?.[ph];
      if (ph === 'cyberattack') {
        merged[ph] = { ...(ai || {}) };
      } else {
        merged[ph] = ai || [];
      }
    }
    onApply(merged);
  };

  const handleApplySelected = () => {
    const merged = {};
    for (const ph of phases) {
      const cur = currentData?.[ph];
      const ai = aiData?.[ph];
      const phSel = selections[ph] || {};

      if (ph === 'cyberattack') {
        const result = { ...(cur || {}) };
        const fields = getFieldsForPhase(ph);
        fields.forEach(f => {
          if (phSel[f.key]) result[f.key] = ai?.[f.key];
        });
        merged[ph] = result;
      } else {
        const curArr = cur || [];
        const aiArr = ai || [];
        const maxLen = Math.max(curArr.length, aiArr.length);
        const result = [];
        for (let i = 0; i < maxLen; i++) {
          const c = curArr[i];
          const a = aiArr[i];
          const itemSel = phSel[i] || {};

          if (!c && a) {
            // New item from AI
            if (itemSel._include) result.push({ ...a });
            continue;
          }
          if (c && !a) {
            // Current item not in AI — always keep
            result.push({ ...c });
            continue;
          }
          // Both exist — merge selected fields
          const item = { ...c };
          const fields = getFieldsForPhase(ph);
          fields.forEach(f => {
            if (itemSel[f.key]) item[f.key] = a[f.key];
          });
          result.push(item);
        }
        merged[ph] = result;
      }
    }
    onApply(merged);
  };

  // Count total diffs across all phases
  const diffCounts = useMemo(() => {
    const counts = {};
    for (const ph of phases) {
      const fields = getFieldsForPhase(ph);
      const cur = currentData?.[ph];
      const ai = aiData?.[ph];
      let diffs = 0;
      if (ph === 'cyberattack') {
        fields.forEach(f => { if (!valuesEqual(cur?.[f.key], ai?.[f.key], f.type)) diffs++; });
      } else {
        const curArr = cur || [];
        const aiArr = ai || [];
        const maxLen = Math.max(curArr.length, aiArr.length);
        for (let i = 0; i < maxLen; i++) {
          if (!curArr[i] || !aiArr[i]) { diffs++; continue; }
          fields.forEach(f => { if (!valuesEqual(curArr[i]?.[f.key], aiArr[i]?.[f.key], f.type)) diffs++; });
        }
      }
      counts[ph] = diffs;
    }
    return counts;
  }, [phases, currentData, aiData]);

  if (!isOpen) return null;

  const renderPhaseContent = (ph) => {
    const fields = getFieldsForPhase(ph);
    const cur = currentData?.[ph];
    const ai = aiData?.[ph];
    const phSel = selections[ph] || {};

    if (ph === 'cyberattack') {
      const diffs = computeObjectDiff(cur || {}, ai || {}, fields);
      return (
        <div className="space-y-2">
          {diffs.map(d => {
            const selected = !!phSel[d.key];
            return (
              <div
                key={d.key}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
                  d.same ? 'border-slate-100 bg-white' : 'border-amber-200 bg-amber-50/50'
                }`}
              >
                <div className="w-32 flex-shrink-0">
                  <span className="text-xs font-medium text-slate-600">{d.label}</span>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <div className="flex-1 text-center">
                    <p className="text-[9px] text-slate-400 mb-0.5">Current</p>
                    {renderValue(d.curVal, d.type)}
                  </div>
                  {!d.same && (
                    <span className="text-slate-300 text-xs flex-shrink-0">&rarr;</span>
                  )}
                  {d.same ? (
                    <div className="flex-1 text-center">
                      <p className="text-[9px] text-slate-400 mb-0.5">AI</p>
                      <span className="text-xs text-slate-400">=</span>
                    </div>
                  ) : (
                    <div className="flex-1 text-center">
                      <p className="text-[9px] text-slate-400 mb-0.5">AI</p>
                      {renderValue(d.aiVal, d.type)}
                    </div>
                  )}
                </div>
                <div className="w-8 flex-shrink-0 text-center">
                  {!d.same && (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleField(ph, null, d.key)}
                      className="w-4 h-4 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky cursor-pointer"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // Array phases (services, processes)
    const curArr = cur || [];
    const aiArr = ai || [];
    const itemDiffs = computeArrayDiff(curArr, aiArr, fields);
    const nameKey = ph === 'services' ? 'service_name' : 'process_name';

    return (
      <div className="space-y-4">
        {itemDiffs.map(item => {
          const itemSel = phSel[item.index] || {};
          const itemName = item.ai?.[nameKey] || item.cur?.[nameKey] || `Item ${item.index + 1}`;

          if (item.isNew) {
            // AI suggests a new item
            return (
              <div key={item.index} className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-emerald-100 text-emerald-700 rounded">New</span>
                    <span className="text-sm font-medium text-slate-700">{itemName}</span>
                  </div>
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <span className="text-[10px] text-slate-500">Include</span>
                    <input
                      type="checkbox"
                      checked={!!itemSel._include}
                      onChange={() => toggleField(ph, item.index, '_include')}
                      className="w-4 h-4 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky"
                    />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 pl-2">
                  {item.fieldDiffs.map(d => (
                    <div key={d.key} className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-500 w-24 flex-shrink-0">{d.label}:</span>
                      {renderValue(d.aiVal, d.type)}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (item.isRemoved) {
            // Current item not in AI suggestions — will be kept
            return (
              <div key={item.index} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 opacity-60">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase bg-slate-200 text-slate-600 rounded">Kept</span>
                  <span className="text-sm font-medium text-slate-600">{itemName}</span>
                </div>
                <p className="text-[10px] text-slate-400 pl-2">Not in AI suggestions — will be preserved</p>
              </div>
            );
          }

          // Both exist — show field-by-field diff
          const hasDiffs = item.fieldDiffs.some(d => !d.same);
          return (
            <div key={item.index} className={`border rounded-lg p-3 ${hasDiffs ? 'border-amber-200 bg-amber-50/30' : 'border-slate-200 bg-white'}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-medium text-slate-700">{itemName}</span>
                {!hasDiffs && <span className="text-[10px] text-slate-400">(no changes)</span>}
              </div>
              <div className="space-y-1.5">
                {item.fieldDiffs.map(d => (
                  <div
                    key={d.key}
                    className={`flex items-center gap-3 px-2 py-1.5 rounded ${
                      d.same ? '' : 'bg-amber-50 border border-amber-100'
                    }`}
                  >
                    <div className="w-28 flex-shrink-0">
                      <span className="text-[10px] font-medium text-slate-500">{d.label}</span>
                    </div>
                    <div className="flex-1 flex items-center gap-2">
                      <div className="flex-1">{renderValue(d.curVal, d.type)}</div>
                      {!d.same && (
                        <>
                          <span className="text-slate-300 text-xs flex-shrink-0">&rarr;</span>
                          <div className="flex-1">{renderValue(d.aiVal, d.type)}</div>
                        </>
                      )}
                    </div>
                    <div className="w-8 flex-shrink-0 text-center">
                      {!d.same && (
                        <input
                          type="checkbox"
                          checked={!!itemSel[d.key]}
                          onChange={() => toggleField(ph, item.index, d.key)}
                          className="w-4 h-4 rounded border-slate-300 text-eucnet-sky focus:ring-eucnet-sky cursor-pointer"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-slate-900 bg-opacity-30 z-40" onClick={onClose} aria-hidden="true" />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <AutoAwesome sx={{ fontSize: 18 }} className="text-eucnet-sky" />
            <h2 className="text-lg font-semibold text-eucnet-navy">
              AI Suggestion Review
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"
            aria-label="Close"
          >
            <span className="text-xl leading-none">&times;</span>
          </button>
        </div>

        {/* Tabs for multi-phase */}
        {isMulti && (
          <div className="flex border-b border-slate-200 px-4">
            {phases.map(ph => (
              <button
                key={ph}
                type="button"
                onClick={() => setActiveTab(ph)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === ph
                    ? 'border-eucnet-sky text-eucnet-navy'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {PHASE_TITLES[ph]}
                {diffCounts[ph] > 0 && (
                  <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-amber-100 text-amber-700 font-bold">
                    {diffCounts[ph]}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Description */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
          <p className="text-xs text-slate-500">
            Compare your current values with AI suggestions. Check the fields you want to apply, then choose an action below.
            {!isMulti && (
              <> Reviewing: <strong>{PHASE_TITLES[activeTab]}</strong></>
            )}
          </p>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderPhaseContent(activeTab)}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleKeepCurrent}
            className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium"
          >
            Keep Current
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleApplySelected}
              className="px-4 py-2 rounded-lg bg-eucnet-sky text-white hover:bg-eucnet-navy text-sm font-medium"
            >
              Apply Selected
            </button>
            <button
              type="button"
              onClick={handleApplyAll}
              className="px-4 py-2 rounded-lg bg-eucnet-navy text-white hover:bg-slate-800 text-sm font-medium"
            >
              Apply All
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AISuggestionReviewModal;
